import { z } from 'zod'
import { normalizePageUrl } from '../sitemap/url'

/**
 * What a scrape addresses, and how long one answer stands in for the next
 * request. What it *costs* is in `../cost/jina`, because the shadow rate is
 * deployment configuration and this is not.
 */

/**
 * The URL, normalised the same way `page.url` is.
 *
 * Reusing `normalizePageUrl` rather than writing a second normaliser is the
 * whole reason the cache is reachable from a site's own pages: `page.url` and
 * `scrape_document.url` are produced by one function, so joining them is an
 * equality test rather than a hope that two spellings agree. A second
 * normaliser here — one that stripped trailing slashes, say — would fork the
 * cache and make every join miss.
 */
export const scrapeUrlSchema = z
  .string()
  .trim()
  .min(1)
  .max(2048)
  .transform((value, ctx) => {
    const url = normalizePageUrl(value)
    if (!url) {
      ctx.addIssue({ code: 'custom', message: `Not a fetchable http(s) URL: ${value}` })
      return z.NEVER
    }
    return url
  })

/**
 * How old a stored copy may be and still answer a new request.
 *
 * A week, and it is the single most important number in this feed. Every
 * workspace scraping the same competitor's pricing page pays once; a fresh
 * request inside the window is a select. `maxAgeHours` on the payload narrows
 * it for a caller who needs something newer, and `force` refuses it outright.
 */
export const SCRAPE_CACHE_TTL_HOURS = 24 * 7

/**
 * The ceiling on how stale a caller may declare acceptable. A year, which is
 * not a cache policy so much as a guard against a payload asking for content
 * from before we existed and reading a null as "this page is empty".
 */
export const SCRAPE_MAX_AGE_HOURS = 24 * 365

/**
 * The reader keeps its own snapshot, which is a second cache behind ours.
 *
 * Verified live: a response can carry `warning: "This is a cached snapshot of
 * the original page"`, so a fetch our cache called a miss may still be answered
 * from theirs. Recorded rather than fought — it is cheaper for both of us — but
 * `force` sends `x-no-cache` so someone who asked for the page as it is right
 * now gets it instead of a snapshot of yesterday.
 */
export const SCRAPE_STALE_SNAPSHOT_WARNING = 'cached snapshot'

/**
 * A ceiling on the body we will store. Markdown of a page runs to tens of
 * kilobytes; four megabytes is a document that is no longer a page, and storing
 * it would put a row in a shared table that every reader of that URL then pays
 * to fetch.
 */
export const SCRAPE_MAX_MARKDOWN_BYTES = 4 * 1024 * 1024
